import React from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import {ConfigProvider, theme, App as AntApp} from 'antd';
import {HelmetProvider} from 'react-helmet-async';
import {authService} from './domains/main/services/api';
import {PageTitleProvider} from './domains/main/context/PageTitleContext';
import {ThemeProvider, useTheme} from './domains/main/context/ThemeContext';

// Layouts
import MainLayout from './domains/main/components/layout/MainLayout';
import PosMainLayout from './domains/device/components/layout/PosMainLayout';


// Pages
import LoginPage from './domains/main/pages/Auth/LoginPage';
import ErrorPage from './domains/main/pages/ErrorPage/ErrorPage';
import CallbackPage from "./domains/main/pages/Auth/CallbackPage";
import CashRegistersList from "./domains/main/pages/CashRegisters/CashRegistersList";
import CashRegisterPage from "./domains/main/pages/CashRegisters/CashRegisterPage";
import CashRegisterEdit from "./domains/main/pages/CashRegisters/CashRegisterEdit";
import CashRegisterCreate from "./domains/main/pages/CashRegisters/CashRegisterCreate";
import CashiersList from "./domains/main/pages/Cashiers/CashiersList";
import CashierPage from "./domains/main/pages/Cashiers/CashierPage";
import CashierEdit from "./domains/main/pages/Cashiers/CashierEdit";
import CashierCreate from "./domains/main/pages/Cashiers/CashierCreate";
import FiscalCashRegistersList from "./domains/main/pages/FiscalCashRegisters/FiscalCashRegistersList";
import FiscalCashRegisterPage from "./domains/main/pages/FiscalCashRegisters/FiscalCashRegisterPage";
import FiscalCashRegisterEdit from "./domains/main/pages/FiscalCashRegisters/FiscalCashRegisterEdit";
import FiscalCashRegisterCreate from "./domains/main/pages/FiscalCashRegisters/FiscalCashRegisterCreate";
import PaymentMethodsList from "./domains/main/pages/PaymentMethods/PaymentMethodsList";
import PaymentMethodPage from "./domains/main/pages/PaymentMethods/PaymentMethodPage";
import PaymentMethodCreate from "./domains/main/pages/PaymentMethods/PaymentMethodCreate";
import PaymentMethodEdit from "./domains/main/pages/PaymentMethods/PaymentMethodEdit";
import TestDeviceInfoPage from "./domains/device/pages/TestDeviceInfoPage";
import DeviceLoginPage from "./domains/device/pages/Auth/DeviceLoginPage";
import PosCashierLoginPage from "./domains/device/pages/Auth/PosCashierLoginPage";
import SalesModePage from "./domains/device/pages/SalesModePage";
import DeviceSalesList from "./domains/device/pages/SalesList";
import SalesList from "./domains/main/pages/Sales/SalesList";
import SalePage from "./domains/main/pages/Sales/SalePage";
import CustomersList from "./domains/main/pages/Customers/CustomersList";
import CustomerPage from "./domains/main/pages/Customers/CustomerPage";
import CustomerEdit from "./domains/main/pages/Customers/CustomerEdit";
import CustomerCreate from "./domains/main/pages/Customers/CustomerCreate";
import KaspiPOSTestPage from "./domains/device/pages/TestKaspiPage";
import SelfServiceWaitingScreen from "./domains/device/pages/SelfService/SelfServiceWaitingScreen";
import SelfServiceShopScreen from "./domains/device/pages/SelfService/SelfServiceShopScreen";
import SelfServicePaymentScreen from "./domains/device/pages/SelfService/SelfServicePaymentScreen";
import SelfServiceCompleteScreen from "./domains/device/pages/SelfService/SelfServiceCompleteScreen";
import SelfServiceAdminMenu from "./domains/device/pages/SelfService/SelfServiceAdminMenu";
import { SelfServiceLanguageProvider } from "./domains/device/context/SelfServiceLanguageContext";
import SelfServiceLayout from "./domains/device/components/layout/SelfServiceLayout";

// Защищенный маршрут, проверяющий авторизацию и необходимость смены временного пароля
const ProtectedRoute = ({children, permission, permissions, requireAll = false, adminRequired = false}) => {
    const isLoggedIn = authService.isAuthenticated();
    const hasTemporaryPassword = authService.hasTemporaryPassword();

    if (!isLoggedIn) {
        return <Navigate to="/login"/>;
    }

    if (hasTemporaryPassword) {
        return <Navigate to="/change-temporary-password"/>;
    }

    // Проверка разрешений
    if (permission && !authService.hasPermission(permission)) {
        return <Navigate to="/forbidden"/>;
    }

    if (permissions) {
        const hasAccess = requireAll
            ? authService.hasAllPermissions(permissions)
            : authService.hasAnyPermission(permissions);

        if (!hasAccess) {
            return <Navigate to="/forbidden"/>;
        }
    }

    return children;
};

// Защищенный маршрут для device (требует авторизацию кассира)
const DeviceProtectedRoute = ({children}) => {
    const cashierInfo = localStorage.getItem('device_cashier_info');

    if (!cashierInfo) {
        return <Navigate to="/device/cashier-login"/>;
    }

    try {
        const parsed = JSON.parse(cashierInfo);
        if (!parsed?.cashier) {
            return <Navigate to="/device/cashier-login"/>;
        }
    } catch (e) {
        return <Navigate to="/device/cashier-login"/>;
    }

    return children;
};

// Защищенный маршрут только для страницы смены временного пароля
const TemporaryPasswordRoute = ({children}) => {
    const isLoggedIn = authService.isAuthenticated();
    const hasTemporaryPassword = authService.hasTemporaryPassword();

    if (!isLoggedIn) {
        return <Navigate to="/login"/>;
    }

    // Если нет временного пароля, то перенаправляем на главную
    if (!hasTemporaryPassword) {
        return <Navigate to="/"/>;
    }

    return children;
};

// Компонент для применения выбранной темы
const ThemedApp = () => {
    const {darkMode} = useTheme();

    return (
        <HelmetProvider>
            <ConfigProvider
                theme={{
                    token: darkMode
                        ? {
                            colorPrimary: '#05D32A',
                            borderRadius: 10,
                            colorBgBase: '#262726',
                        }
                        : {
                            colorPrimary: '#05D32A',
                            borderRadius: 10,
                        },
                    algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
                    cssVar: true,
                }}
                componentSize="large"
            >

            {/* Добавляем AntApp для обеспечения контекста сообщений */}
                <AntApp>
                    <PageTitleProvider>
                        <Router>
                            <Routes>
                                {/* Страница входа */}
                                <Route path="/login" element={
                                    authService.isAuthenticated() ?
                                        <Navigate to="/sales" /> :
                                        <LoginPage />
                                } />

                                {/* Страница обработки OAuth callback */}
                                <Route path="/callback" element={<CallbackPage />} />

                                {/* Основной лейаут с вложенными маршрутами */}
                                <Route path="/" element={<MainLayout/>}>
                                    {/* Редирект с / на /sales */}
                                    <Route index element={<Navigate to="/sales" replace />} />

                                    {/* Список касс */}
                                    <Route path="/cash-registers" element={
                                        <ProtectedRoute permission="pos:cash_registers:view">
                                            <CashRegistersList/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница кассы */}
                                    <Route path="/cash-registers/:id" element={
                                        <ProtectedRoute permission="pos:cash_registers:view">
                                            <CashRegisterPage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница редактирования кассы */}
                                    <Route path="/cash-registers/:id/edit" element={
                                        <ProtectedRoute permission="pos:cash_registers:edit">
                                            <CashRegisterEdit/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Создание кассы */}
                                    <Route path="/cash-registers/create" element={
                                        <ProtectedRoute permission="pos:cash_registers:create">
                                            <CashRegisterCreate/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Список кассиров */}
                                    <Route path="/cashiers" element={
                                        <ProtectedRoute permission="pos:cashiers:view">
                                            <CashiersList/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница кассира */}
                                    <Route path="/cashiers/:id" element={
                                        <ProtectedRoute permission="pos:cashiers:view">
                                            <CashierPage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница редактирования кассира */}
                                    <Route path="/cashiers/:id/edit" element={
                                        <ProtectedRoute permission="pos:cashiers:edit">
                                            <CashierEdit/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Список создания кассира */}
                                    <Route path="/cashiers/create" element={
                                        <ProtectedRoute permission="pos:cashiers:create">
                                            <CashierCreate/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Список фискальных касс */}
                                    <Route path="/fiscal-cash-registers" element={
                                        <ProtectedRoute permission="pos:fiscal_cash_registers:view">
                                            <FiscalCashRegistersList/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница фискальной кассы */}
                                    <Route path="/fiscal-cash-registers/:id" element={
                                        <ProtectedRoute permission="pos:fiscal_cash_registers:view">
                                            <FiscalCashRegisterPage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница редактирования фискальной кассы */}
                                    <Route path="/fiscal-cash-registers/:id/edit" element={
                                        <ProtectedRoute permission="pos:fiscal_cash_registers:edit">
                                            <FiscalCashRegisterEdit/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Список создания фискальной кассы */}
                                    <Route path="/fiscal-cash-registers/create" element={
                                        <ProtectedRoute permission="pos:fiscal_cash_registers:create">
                                            <FiscalCashRegisterCreate/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Список способов оплаты */}
                                    <Route path="/payment-methods" element={
                                        <ProtectedRoute permission="pos:payment_methods:view">
                                            <PaymentMethodsList/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница способа оплаты */}
                                    <Route path="/payment-methods/:id" element={
                                        <ProtectedRoute permission="pos:payment_methods:view">
                                            <PaymentMethodPage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница редактирования способа оплаты */}
                                    <Route path="/payment-methods/:id/edit" element={
                                        <ProtectedRoute permission="pos:payment_methods:edit">
                                            <PaymentMethodEdit/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница создания способа оплаты */}
                                    <Route path="/payment-methods/create" element={
                                        <ProtectedRoute permission="pos:payment_methods:create">
                                            <PaymentMethodCreate/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Список способов оплаты */}
                                    <Route path="/sales" element={
                                        <ProtectedRoute permission="pos:sales:view">
                                            <SalesList/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница продажи */}
                                    <Route path="/sales/:id" element={
                                        <ProtectedRoute permission="pos:sales:view">
                                            <SalePage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Список клиентов */}
                                    <Route path="/customers" element={
                                        <ProtectedRoute permission="pos:customers:view">
                                            <CustomersList/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница клиента */}
                                    <Route path="/customers/:id" element={
                                        <ProtectedRoute permission="pos:customers:view">
                                            <CustomerPage/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница edit клиента */}
                                    <Route path="/customers/:id/edit" element={
                                        <ProtectedRoute permission="pos:customers:edit">
                                            <CustomerEdit/>
                                        </ProtectedRoute>
                                    }/>

                                    {/* Страница create клиента */}
                                    <Route path="/customers/create" element={
                                        <ProtectedRoute permission="pos:customers:create">
                                            <CustomerCreate/>
                                        </ProtectedRoute>
                                    }/>


                                    {/* SYSTEM PAGES */}

                                    {/* 404 ошибка */}
                                    <Route path="*" element={<ErrorPage
                                        statusCode={404}
                                        title="Страница не найдена"
                                        description="Такой страницы не существует"
                                    />}/>

                                    {/* Для отображения ошибок с сервера */}
                                    <Route path="/error" element={<ErrorPage
                                        statusCode={500}
                                        title="Ошибка сервера"
                                        description="Произошла ошибка. Попробуйте обновить страницу или вернуться позже."
                                    />}/>

                                    {/* Для ошибки доступа */}
                                    <Route path="/forbidden" element={<ErrorPage
                                        statusCode={403}
                                        title="Доступ запрещен"
                                        description="У вас недостаточно прав для просмотра данной страницы"
                                    />}/>
                                </Route>

                                {/* DEVICE PAGES */}

                                <Route path="/device" element={<PosMainLayout />}>

                                    <Route path="login" element={<DeviceLoginPage/>}/>
                                    <Route path="cashier-login" element={<PosCashierLoginPage/>}/>
                                    <Route path="sale" element={<SalesModePage/>}/>
                                    <Route path="sales" element={<DeviceSalesList/>}/>
                                    <Route path="test" element={<TestDeviceInfoPage/>}/>
                                    <Route path="test-kaspi" element={<KaspiPOSTestPage/>}/>

                                </Route>

                                {/* SELF-SERVICE (КСО) PAGES - Требует авторизацию кассира */}
                                <Route path="/device/self-service" element={
                                    <DeviceProtectedRoute>
                                        <SelfServiceLanguageProvider>
                                            <SelfServiceLayout />
                                        </SelfServiceLanguageProvider>
                                    </DeviceProtectedRoute>
                                }>
                                    <Route index element={<SelfServiceWaitingScreen/>}/>
                                    <Route path="shop" element={<SelfServiceShopScreen/>}/>
                                    <Route path="payment" element={<SelfServicePaymentScreen/>}/>
                                    <Route path="complete" element={<SelfServiceCompleteScreen/>}/>
                                    <Route path="admin" element={<SelfServiceAdminMenu/>}/>
                                </Route>
                            </Routes>
                        </Router>
                    </PageTitleProvider>
                </AntApp>
            </ConfigProvider>
        </HelmetProvider>
    );
};

function App() {
    return (
        <ThemeProvider>
            <ThemedApp/>
        </ThemeProvider>
    );
}

export default App;